import React from 'react';
import { MobxComponent } from "../../../mobx/components";
import { inject, observer } from "mobx-react";
import { withTranslation, WithTranslation } from "react-i18next";
import { IconSettings, IconClose, IconDelete, IconWrapperRounded, IconKiosk, IconPickup, IconDelivery, IconDineIn, IconArrowForward, IconKioskCloud, IconCloudWrapper, IconCloud } from "./icons";
import { ColorPicker, Switch } from "@lib/components";
import { HeaderTitleWrapper, MenuItem, QuickSettingsContainer, QuickSettingsHeaderWrapper, QuickSettingsBodyWrapper, QuickSettingsFooterWrapper, QuickSettingsButton, QuickSettingsItemsContainer, QuickSettingsItemsWrapper, QuickSettingsItemWrapper, ButtonV2, MenuColorHeaderWrapper, MenuColorHeaderTitleWrapper, MenuColorDescription, MenuColorBodyWrapper, MenuColorFormGroup, MenuColorLabel, MenuColorColorPickerWrapper, MenuColorColorPickerText, MenuColorPresetsList, MenuColorPresetsListItem, MenuColorPresetsListItemContent, MenuColorFooterWrapper, MenuToggleContainer, MenuToggleText, MenuServiceTypeContainer, MenuServiceTypeItem, MenuLineSeparator, MenuPublishPlatformContainer, MenuPublishPlatFormItem, MenuPublishPlatFormItemHeader, MenuPublishPlatFormItemHeaderLabel, MenuStatusAlert, MenuStatusAlertText, MenuStatusLabelAvailable, MenuStatusServiceList, MenuStatusServiceListItem, MenuStatusServiceListRow, MenuStatusShowSettingsContainer, MenuStatusShowSettingsText } from "./layout.c";
import { IconKioskWrapper } from '../../ui/restaurant/views/menus-v2/item-v2';
import {
    effectiveCategoryAvailabilityId,
    effectiveItemAvailabilityId,
} from './menuvisibility';

function getActiveServiceValuesFromConditions(conditions: any): string[] {
    if (conditions == null || typeof conditions !== 'object') return [];
    const pos = conditions.pos?.enabled ? (conditions.pos.services || []) : [];
    const online = conditions.online?.enabled ? (conditions.online.services || []) : [];
    return Array.from(new Set([...(pos as string[]), ...(online as string[])])) as string[];
}

function getAvailabilityRowById(menu: any, id: string | undefined): any | null {
    if (!id || !menu?.availabilities?.length) return null;
    return menu.availabilities.find((a: any) => a.id === id) ?? null;
}

/** POS or Online is on and has at least one service type selected. */
function availabilityHasEnabledServiceTypes(row: any): boolean {
    if (!row || typeof row !== 'object') return false;
    const pos = row.pos;
    const online = row.online;
    const posOk =
        !!pos?.enabled && Array.isArray(pos.services) && pos.services.filter(Boolean).length > 0;
    const onlineOk =
        !!online?.enabled &&
        Array.isArray(online.services) &&
        online.services.filter(Boolean).length > 0;
    return posOk || onlineOk;
}

interface Props extends WithTranslation {
    show?: boolean;
    onHide?: () => void;
    onSave: (color: string) => void;
    onShowSettings?: () => void;
    title?: string;
    value?: string;
    conditions?: any;
    type?: 'menu' | 'category' | 'item';
    /** When set with v2 menus, counts only descendants whose resolved availability has enabled POS/online service types. */
    menuId?: string;
    categoryId?: string;
    /** @deprecated Prefer menuId — used only when menuId is missing */
    categoryCount?: number;
    /** @deprecated Prefer menuId — used only when menuId is missing */
    itemCount?: number;
}
interface State {
    color: string;
}

@inject("store") @observer
class MenuPreVisibilityClass extends MobxComponent<Props, State> {
    constructor(props: Props) {
		super(props);
		this.state = {
            color: props.value || '#000000'
        };
	}
   
    render() {
        const { store, t} = this.injected;
        const {
            show,
            onHide,
            onSave,
            title,
            onShowSettings,
            conditions,
            type,
            menuId,
            categoryId,
            categoryCount: categoryCountProp,
            itemCount: itemCountProp,
        } = this.props;
        const { color } = this.state;
        const services = getActiveServiceValuesFromConditions(conditions);

        const menusV2 = (store.restaurant?.menus_v2 ?? []) as any[];
        const menu = menuId ? menusV2.find((m: any) => m.id === menuId) : null;

        let categoryCount = categoryCountProp ?? 0;
        let itemCount = itemCountProp ?? 0;
        if (menu && type === 'menu') {
            categoryCount = (menu.categories ?? []).filter((cat: any) =>
                availabilityHasEnabledServiceTypes(
                    getAvailabilityRowById(menu, effectiveCategoryAvailabilityId(menu, cat))
                )
            ).length;
            const catIds = new Set((menu.categories ?? []).map((c: any) => c.id));
            itemCount = (menu.items ?? []).filter((it: any) => {
                if (!it?.category_id || !catIds.has(it.category_id)) return false;
                const cat = menu.categories.find((c: any) => c.id === it.category_id);
                return availabilityHasEnabledServiceTypes(
                    getAvailabilityRowById(menu, effectiveItemAvailabilityId(menu, cat, it))
                );
            }).length;
        } else if (menu && type === 'category' && categoryId) {
            const cat = menu.categories?.find((c: any) => c.id === categoryId);
            itemCount = (menu.items ?? [])
                .filter((it: any) => it.category_id === categoryId)
                .filter((it: any) =>
                    availabilityHasEnabledServiceTypes(
                        getAvailabilityRowById(menu, effectiveItemAvailabilityId(menu, cat, it))
                    )
                ).length;
        }

        const showMenuScopeCounts =
            type === 'menu' && services.length > 0 && (categoryCount > 0 || itemCount > 0);
        const showCategoryScopeCounts = type === 'category' && services.length > 0 && itemCount > 0;

        return (
        <QuickSettingsContainer className={show ? 'active': ''}>
            <MenuColorHeaderWrapper>
                <MenuColorHeaderTitleWrapper>
                    {title || 'Acme Pizza'}
                </MenuColorHeaderTitleWrapper>
                <MenuItem onClick={onHide}><IconClose /></MenuItem>
            </MenuColorHeaderWrapper>
            <MenuColorBodyWrapper style={{minHeight: '50vh', overflowY:'scroll'}}>
                <MenuStatusAlert>
                    {conditions?.pos?.enabled && conditions.online?.enabled && ( <>
                        <IconKioskCloud />
                         <MenuStatusAlertText>This {type} is active on both POS/Online </MenuStatusAlertText>
                        </>
                    )}
                    {conditions?.pos?.enabled && !conditions?.online?.enabled && <>
                    <IconKioskWrapper>
                        <IconKiosk />
                    </IconKioskWrapper> 
                     <MenuStatusAlertText>This {type} is active POS only</MenuStatusAlertText>
                    </>}
                    {!conditions?.pos?.enabled && conditions?.online?.enabled && <>
                    <IconCloudWrapper>
                        <IconCloud />
                    </IconCloudWrapper>
                    <MenuStatusAlertText>This {type} is active Online only</MenuStatusAlertText>
                    </>}
                    {/* <IconKioskWrapper>
                        <IconKiosk />
                    </IconKioskWrapper> */}
                   
                </MenuStatusAlert>

                {showMenuScopeCounts && (
                    <MenuColorLabel>
                        {categoryCount} categor{categoryCount === 1 ? 'y' : 'ies'} and {itemCount} item
                        {itemCount === 1 ? '' : 's'} with active service types — available for
                    </MenuColorLabel>
                )}
                {showCategoryScopeCounts && (
                    <MenuColorLabel>
                        {itemCount} item{itemCount === 1 ? '' : 's'} with active service types — available for
                    </MenuColorLabel>
                )}
                {services.length === 0 && (
                    <MenuColorLabel>No active service types found.</MenuColorLabel>
                )}
               <MenuStatusServiceList>
                    
                    {conditions?.pos?.enabled && conditions.online?.enabled && services.length > 0 && ( 
                    <MenuStatusServiceListRow>
                        {services.map((service: string) => (
                            <MenuStatusServiceListItem key={service}>{
                                service === 'dine_in' ? <IconDineIn /> : service === 'pickup' ? <IconPickup /> : service === 'delivery' ? <IconDelivery /> : <IconKiosk />
                            } {service.charAt(0).toUpperCase() + service.slice(1).replace('_', '-')}</MenuStatusServiceListItem>  
                        ))}
                    </MenuStatusServiceListRow>
                    )}
                    {conditions?.pos?.enabled && !conditions.online?.enabled && (
                        <MenuStatusServiceListRow>
                            {conditions?.pos?.services?.map((service: string) => (
                                <MenuStatusServiceListItem key={service}>
                                    {service === 'dine_in' ? <IconDineIn /> : service === 'pickup' ? <IconPickup /> : service === 'delivery' ? <IconDelivery /> : <IconKiosk />
                                    } {service.charAt(0).toUpperCase() + service.slice(1).replace('_', '-')}</MenuStatusServiceListItem>
                            ))}
                        </MenuStatusServiceListRow>
                    )}
                    {!conditions?.pos?.enabled && conditions?.online?.enabled && (
                        <MenuStatusServiceListRow>
                            {conditions?.online?.services?.map((service: string) => (
                                <MenuStatusServiceListItem key={service}>
                                    {service === 'dine_in' ? <IconDineIn /> : service === 'pickup' ? <IconPickup /> : service === 'delivery' ? <IconDelivery /> : <IconCloud />
                                    } {service.charAt(0).toUpperCase() + service.slice(1).replace('_', '-')}</MenuStatusServiceListItem>
                            ))}
                        </MenuStatusServiceListRow>
                    )}
               </MenuStatusServiceList>
               <MenuStatusShowSettingsContainer
                    onClick={onShowSettings}>
                    <MenuStatusShowSettingsText>Show settings</MenuStatusShowSettingsText>
                    <IconArrowForward />
               </MenuStatusShowSettingsContainer>
            </MenuColorBodyWrapper>
        </QuickSettingsContainer>
        )
    }
}
export const MenuPreVisibility = withTranslation()(MenuPreVisibilityClass);
